import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { getCategoryImage, type MegaColumn } from "@/lib/catalog";
import { cdnImage, IMG } from "@/lib/img";
import { CartFlyout, CartToast, useCart } from "@/features/cart";
import { Logo } from "./Logo";
import { MobileMenu } from "./MobileMenu";
import { CATEGORIES, FIRST_RAIL, GROUP_LABEL, RAIL } from "./nav-data";
import { useNavSections } from "@/lib/tile-relevance";
import { UspRow } from "@/features/home/UspRow";
import { SearchBar } from "./SearchBar";
import { SiteFooter, FooterLegalBar } from "./SiteFooter";
import { CookieBanner } from "./CookieBanner";
import { ScrollToTop } from "./ScrollToTop";
// Rechtstreeks uit ./auth, niet de features/account-barrel: SiteLayout staat op élke pagina, en
// een barrel-import van ook maar één export dwingt Rollup het hele bestand — incl. alle (lazy-
// bedoelde) accountpagina's en hún firebase/functions-imports — in de kritieke bundel.
import { useCustomerAuth } from "@/features/account/auth";
import { UspTicker } from "./UspTicker";
import { SITE_URL } from "./Seo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useT } from "@/lib/i18n";
import { useCatalogOverrides } from "@/lib/catalog-overrides";
import { COMPANY, COMPANY_PROFILES, isPlaceholder } from "@/lib/company";

// Navigatie-data (categorie-balk, rail, panes) is gedeeld met MobileMenu: zie ./nav-data.

/**
 * Wie wij zijn, voor Google. Een Store (winkel met een adres) in plaats van een kale Organization:
 * dat voedt het kennispaneel en de lokale resultaten. Gegevens die nog niet zijn ingevuld in
 * company.ts laten we weg — een leeg veld is erger dan geen veld.
 */
const ORG_JSONLD = (() => {
  const adres = [
    isPlaceholder(COMPANY.street) ? null : ["streetAddress", COMPANY.street] as const,
    isPlaceholder(COMPANY.postalCode) ? null : ["postalCode", COMPANY.postalCode] as const,
    isPlaceholder(COMPANY.city) ? null : ["addressLocality", COMPANY.city] as const,
  ].filter(Boolean) as (readonly [string, string])[];
  return {
    "@context": "https://schema.org",
    "@type": adres.length ? "Store" : "Organization",
    name: COMPANY.name,
    legalName: COMPANY.legalName,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    image: `${SITE_URL}/logo.png`,
    description: "Glas op maat en glasartikelen — op de millimeter op maat gemaakt, verzekerd verzonden.",
    email: COMPANY.email,
    ...(isPlaceholder(COMPANY.phone) ? {} : { telephone: COMPANY.phone }),
    ...(isPlaceholder(COMPANY.vat) ? {} : { vatID: COMPANY.vat }),
    ...(isPlaceholder(COMPANY.kvk) ? {} : { identifier: { "@type": "PropertyValue", name: "KvK", value: COMPANY.kvk } }),
    ...(adres.length
      ? { address: { "@type": "PostalAddress", ...Object.fromEntries(adres), addressCountry: "NL" } }
      : {}),
    areaServed: [
      { "@type": "Country", name: "Nederland" },
      { "@type": "Country", name: "België" },
    ],
    // Openingstijden van de klantenservice; het afhaalpunt heeft zijn eigen tijden op /levertijden.
    openingHours: "Mo-Fr 09:00-17:00",
    currenciesAccepted: "EUR",
    paymentAccepted: "iDEAL, Bancontact, creditcard, bankoverschrijving",
    // Onze profielen elders (LinkedIn, Kiyoh, Google-bedrijfsprofiel). Hiermee koppelt Google deze
    // site aan dezelfde bedrijfsentiteit — dat voedt het kennispaneel en Maps. Bewust GEEN
    // aggregateRating hier: sterren voor je eigen bedrijf op je eigen site zijn "self-serving" en
    // door Google uitgesloten; winkelbeoordelingen horen thuis in de verkopersbeoordeling.
    ...(COMPANY_PROFILES.length ? { sameAs: [...COMPANY_PROFILES] } : {}),
  };
})();
const WEBSITE_JSONLD = { "@context": "https://schema.org", "@type": "WebSite", name: "Glastotaal", url: SITE_URL };

// Buttons sit on the white bar → donkere tekst/iconen, subtiele lichtblauwe hover.
const iconBtn =
  "relative inline-flex h-11 w-11 items-center justify-center text-primary-700 transition hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500";

/** Public site chrome: header (menu · search · account · cart) + footer. */
export function SiteLayout() {
  const t = useT();
  // Nodig voor de nav-fototjes: uitgezette soorten/uitvoeringen mogen de tegelfoto niet bepalen.
  const catalogOv = useCatalogOverrides();
  const { count } = useCart();
  const customer = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [stripCat, setStripCat] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState(FIRST_RAIL.slug);
  const [activeSection, setActiveSection] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  // Close all popovers on route change.
  useEffect(() => {
    setMenuOpen(false);
    setCartOpen(false);
    setAccountOpen(false);
    setStripCat(null);
  }, [location.pathname, location.search]);

  // Bij een paginawissel terug naar de bovenkant scrollen — anders lijkt het (vanaf onderaan
  // een pagina) alsof er niets gebeurt. Ook op `search` (drill-down binnen een categorie,
  // ?groep=/?merk=/?q= — zelfde pad, andere inhoud), maar NIET bij een #anchor-sprong
  // (jump-chips) — die moet juist naar de sectie scrollen. rAF zodat het ná de render valt
  // (lazy routes), en zowel window als het document-element voor de zekerheid.
  useEffect(() => {
    if (location.hash) return;
    const toTop = () => { window.scrollTo(0, 0); document.documentElement.scrollTop = 0; };
    toTop();
    const id = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(id);
  }, [location.pathname, location.search, location.hash]);

  // Close the account popover on outside click.
  useEffect(() => {
    if (!accountOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [accountOpen]);

  // Global Organization + WebSite JSON-LD. Verwijder eerst een eventueel vooraf-
  // gerenderde (prerender) kopie zodat we niet dubbelen bij JS-bezoekers.
  useEffect(() => {
    document.head.querySelectorAll('script[data-seo-global="1"]').forEach((el) => el.remove());
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-global", "1");
    script.textContent = JSON.stringify([ORG_JSONLD, WEBSITE_JSONLD]);
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  // Mega-menu cascade: rail (niveau 0) → secties (niveau 1) → items (niveau 2).
  const activeRail = RAIL.find((r) => r.slug === activeCat) ?? FIRST_RAIL;
  // Foto's en volgorde volgen de relevantie-score (zie tile-relevance.ts): de tegel toont het
  // populairste product uit die kolom en de best lopende kolom staat vooraan.
  const sections: MegaColumn[] = useNavSections(menuOpen ? activeCat : null);
  const stripSections = useNavSections(stripCat);
  const section = sections[activeSection] ?? sections[0];
  const openCat = (slug: string): void => { setActiveCat(slug); setActiveSection(0); };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Blauwe huisstijl-balk met roulerende USP's */}
      <UspTicker />

      <header className="relative z-30 border-b border-primary-100 bg-white text-primary-900">
        <div className="relative mx-auto flex max-w-[1600px] items-center gap-x-1 px-2 py-2.5 sm:gap-x-4 sm:px-6 sm:py-3.5 lg:px-8">
          {/* Hamburger links (mobiel) — conventie van grote webshops */}
          <button
            type="button"
            onClick={() => { setMenuOpen((o) => !o); openCat(FIRST_RAIL.slug); }}
            aria-expanded={menuOpen}
            aria-label={t("Menu")}
            className={`${iconBtn} shrink-0 lg:hidden`}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>

          {/* Officieel Glastotaal-logo — mobiel exact gecentreerd (Coolblue-stijl) */}
          <Link to="/" aria-label={t("Naar de homepage")} className="absolute left-1/2 -translate-x-1/2 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:static sm:translate-x-0">
            <Logo className="h-8 sm:h-9" />
          </Link>

          {/* Hoofd-nav links naast het logo (desktop) — ruim verdeeld (IKEA-stijl) */}
          <nav className="hidden lg:flex items-center gap-x-2 lg:ml-4">
            {([
              { label: "Acties", to: "/acties" },
              { label: "Kennisbank", to: "/kennisbank" },
              { label: "Contact", to: "/contact" },
            ] as const).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `border-b-[3px] px-3 py-2 text-sm transition whitespace-nowrap ${
                    isActive
                      ? "border-accent-500 font-semibold text-primary-900"
                      : "border-transparent font-medium text-primary-600 hover:border-accent-400 hover:text-primary-900"
                  }`
                }
              >
                {t(l.label)}
              </NavLink>
            ))}
          </nav>

          {/* Search (flex-1 op desktop; eigen rij op mobile) — ruim los van nav en cluster */}
          <div className="hidden flex-1 sm:block lg:mx-6">
            <SearchBar />
          </div>

          {/* Rechter cluster: taal · account · winkelwagen — gegroepeerd en ruim los van de balk */}
          <div className="ml-auto flex shrink-0 items-center gap-x-1 sm:ml-0 sm:gap-x-2 lg:gap-x-3 lg:pl-2">

          {/* Taalkiezer (desktop) */}
          <LanguageSwitcher tone="header" className="hidden shrink-0 lg:block" />

          {/* Account */}
          <div ref={accountRef} className="relative shrink-0">
            <button
              type="button"
              onClick={() => setAccountOpen((o) => !o)}
              aria-expanded={accountOpen}
              aria-label={customer.signedIn ? t("Account (ingelogd)") : t("Account")}
              className={`${iconBtn} relative`}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
              {/* Subtiel statusbolletje op het poppetje: groen = ingelogd, kruisje = niet ingelogd. */}
              {customer.signedIn ? (
                <span className="pointer-events-none absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-success-500 ring-2 ring-white" aria-hidden="true" />
              ) : (
                <span className="pointer-events-none absolute right-0 top-0 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-danger-500 ring-2 ring-white" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </span>
              )}
            </button>
            {accountOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-64 border border-primary-100 bg-white p-4 text-sm shadow-xl">
                {customer.signedIn ? (
                  <>
                    <p className="truncate font-semibold text-primary-900">{customer.user?.displayName || customer.user?.email}</p>
                    <nav className="mt-3 flex flex-col">
                      <Link to="/account" className="-mx-2 px-2 py-1.5 font-medium text-primary-700 hover:bg-primary-50">{t("Mijn account")}</Link>
                      <Link to="/account/bestellingen" className="-mx-2 px-2 py-1.5 font-medium text-primary-700 hover:bg-primary-50">{t("Mijn bestellingen")}</Link>
                      <Link to="/account/adressen" className="-mx-2 px-2 py-1.5 font-medium text-primary-700 hover:bg-primary-50">{t("Adresboek")}</Link>
                      <button type="button" onClick={() => customer.logout()} className="-mx-2 mt-1 border-t border-primary-100 px-2 pb-1.5 pt-2 text-left font-semibold text-danger-700 hover:bg-danger-50">{t("Uitloggen")}</button>
                    </nav>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-primary-900">{t("Mijn account")}</p>
                    <p className="mt-1 text-primary-600">{t("Log in voor uw bestellingen, adressen en snellere checkout.")}</p>
                    <Link to="/inloggen" className="mt-3 flex min-h-10 items-center justify-center bg-accent-700 px-4 font-semibold text-white transition hover:bg-accent-800">{t("Inloggen")}</Link>
                    <Link to="/registreren" className="mt-2 block text-center font-medium text-accent-700 hover:text-accent-700">{t("Account aanmaken")}</Link>
                    <Link to="/herroepen" className="mt-3 block border-t border-primary-100 pt-3 font-medium text-primary-600 hover:text-primary-900">{t("Een bestelling herroepen")}</Link>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Cart */}
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setCartOpen((o) => !o)}
              aria-label={`${t("Winkelwagen")}${count > 0 ? ` (${count})` : ""}`}
              aria-expanded={cartOpen}
              className={iconBtn}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="9" cy="20" r="1.4" />
                <circle cx="17" cy="20" r="1.4" />
                <path d="M2 3h2.2l2.2 11.2a1.5 1.5 0 0 0 1.5 1.2h8.4a1.5 1.5 0 0 0 1.5-1.2L20 7H5.2" />
              </svg>
              {count > 0 && (
                <span className="absolute right-0.5 top-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-700 px-1 text-xs font-bold text-white">
                  {count}
                </span>
              )}
            </button>
            {cartOpen && (
              <CartFlyout
                onClose={() => setCartOpen(false)}
                onViewCart={() => { setCartOpen(false); navigate("/winkelwagen"); }}
                onCheckout={() => { setCartOpen(false); navigate("/afrekenen"); }}
              />
            )}
          </div>

          </div>
        </div>

        {/* Search on its own row (mobile) */}
        <div className="px-4 pb-2.5 sm:hidden">
          <SearchBar />
        </div>

        {/* Menu overview panel — mobiel: fullscreen drill-down; desktop: 3-panes cascade */}
        {menuOpen && (
          <>
            <MobileMenu onClose={() => setMenuOpen(false)} />
            <div className="fixed inset-0 z-20 hidden bg-black/30 lg:block" onClick={() => setMenuOpen(false)} aria-hidden />
            <div className="absolute left-0 right-0 z-30 hidden border-t border-primary-100 bg-white shadow-xl lg:block">
              <div className="mx-auto max-w-[1600px]">
                <div className="flex flex-col lg:h-[min(70vh,520px)] lg:flex-row">
                {/* Pane A — hoofdcategorieën (niveau 0) */}
                <div className="shrink-0 overflow-y-auto border-primary-100 lg:w-64 lg:border-r">
                  <div className="py-3">
                    {(["glas", "beslag"] as const).map((g) => (
                      <div key={g}>
                        <p className="px-5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-primary-600">{t(GROUP_LABEL[g])}</p>
                        <ul>
                          {RAIL.filter((r) => r.group === g).map((r) => {
                            const img = getCategoryImage(r.slug, catalogOv);
                            return (
                              <li key={r.slug}>
                                <Link
                                  to={r.to}
                                  onMouseEnter={() => openCat(r.slug)}
                                  onFocus={() => openCat(r.slug)}
                                  className={`flex items-center gap-3 px-4 py-2 text-sm font-medium transition ${
                                    activeCat === r.slug ? "bg-primary-50 text-primary-900" : "text-primary-700 hover:bg-primary-50"
                                  }`}
                                >
                                  <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-primary-100 bg-white">
                                    {img && <img src={cdnImage(img, { w: IMG.thumb })} alt="" loading="lazy" decoding="async" width={36} height={36} className="h-full w-full object-contain p-0.5" />}
                                  </span>
                                  <span className="flex-1 leading-tight">{t(r.label)}</span>
                                  <span aria-hidden className="text-primary-300">›</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pane B — subcategorieën van de actieve categorie (niveau 1) */}
                <div className="hidden shrink-0 overflow-y-auto border-primary-100 lg:block lg:w-72 lg:border-r">
                  <div className="flex items-baseline justify-between px-5 pb-1 pt-4">
                    <h2 className="text-sm font-bold text-primary-900">{t(activeRail.label)}</h2>
                    <Link to={activeRail.to} className="text-xs font-semibold text-accent-700 hover:text-accent-700">{t("Alles")} →</Link>
                  </div>
                  {sections.length > 0 ? (
                    <ul className="pb-3">
                      {sections.map((s, i) => (
                        <li key={s.title ?? i}>
                          <Link
                            to={s.titleTo ?? activeRail.to}
                            onMouseEnter={() => setActiveSection(i)}
                            onFocus={() => setActiveSection(i)}
                            className={`flex items-center justify-between gap-2 px-5 py-2 text-sm transition ${
                              i === activeSection ? "bg-primary-50 font-semibold text-primary-900" : `${s.soon ? "text-primary-600" : "text-primary-700"} hover:bg-primary-50`
                            }`}
                          >
                            <span className="truncate">{t(s.title ?? "")}</span>
                            {s.soon ? (
                              <span className="shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary-700">{t("Binnenkort")}</span>
                            ) : (
                              s.links.length > 0 && <span aria-hidden className="text-primary-300">›</span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="m-5 rounded-xl bg-primary-50 p-4">
                      <span className="inline-block rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-600">{t("Binnenkort beschikbaar")}</span>
                      <p className="mt-2 text-sm text-primary-600">
                        {t("Deze categorie komt eraan.")} <Link to="/contact" className="font-semibold text-primary-700 underline">{t("Neem contact op")}</Link>.
                      </p>
                    </div>
                  )}
                </div>

                {/* Pane C — items van de actieve subcategorie (niveau 2). Leeg als de
                    subcategorie geen verder niveau heeft (directe link of binnenkort). */}
                <div className="hidden flex-1 flex-col overflow-y-auto p-6 lg:flex">
                  {section && section.links.length > 0 && (
                    <>
                      <div className="flex items-baseline justify-between gap-2">
                        <h3 className="text-base font-bold text-primary-900">{t(section.title ?? "")}</h3>
                        <Link to={section.titleTo ?? activeRail.to} className="shrink-0 text-sm font-semibold text-accent-700 hover:text-accent-700">{t("Alles")} →</Link>
                      </div>
                      <ul className="mt-3 max-w-md space-y-0.5">
                        {section.links.map((l) => (
                          <li key={l.label}>
                            <Link to={l.to} className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm hover:bg-primary-50 ${l.soon ? "text-primary-600" : "text-primary-700 hover:text-primary-900"}`}>
                              <span className="truncate">{t(l.label)}</span>
                              {l.soon && <span className="shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary-700">{t("Binnenkort")}</span>}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                </div>

                {/* Footer over de hele kaart */}
                <div className="hidden flex-wrap items-center gap-x-5 gap-y-2 border-t border-primary-100 px-6 py-3 text-sm lg:flex">
                  <Link to="/kennisbank" className="font-medium text-primary-700 hover:text-primary-900">{t("Kennisbank")}</Link>
                  <Link to="/levertijden" className="font-medium text-primary-700 hover:text-primary-900">{t("Levertijden & verzending")}</Link>
                  <Link to="/contact" className="font-medium text-primary-700 hover:text-primary-900">{t("Contact")}</Link>
                  <Link to="/glassoorten" className="ml-auto inline-flex min-h-9 items-center rounded-lg bg-accent-700 px-4 font-semibold text-white transition hover:bg-accent-800">{t("Glas samenstellen")} →</Link>
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Categorie-balk met mega-dropdown op hover (los van het hamburgermenu). */}
      <div className="relative" onMouseLeave={() => setStripCat(null)}>
        <nav aria-label="Categorieën" className="hidden border-b border-primary-100 bg-white lg:block">
          {/* Mobiel: geen categorie-balk — categorieën zitten in het hamburgermenu (Coolblue-stijl) */}
          {/* Desktop: tab-stijl met hover-dropdown */}
          <ul className="mx-auto hidden max-w-[1600px] items-center gap-1 overflow-x-auto px-4 sm:px-6 lg:flex lg:px-8">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <NavLink
                  to={c.to}
                  onMouseEnter={() => setStripCat(c.slug)}
                  onFocus={() => setStripCat(c.slug)}
                  className={({ isActive }) =>
                    `block whitespace-nowrap border-b-[3px] px-3 py-2.5 text-sm transition ${
                      isActive || stripCat === c.slug
                        ? "border-accent-500 font-semibold text-primary-900"
                        : "border-transparent font-medium text-primary-700 hover:border-accent-200 hover:text-accent-700"
                    }`
                  }
                >
                  {t(c.label)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mega-dropdown: compacte foto-tegels van de subcategorieën (desktop, op hover) */}
        {stripCat && stripSections.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-40 hidden border-b border-primary-100 bg-white shadow-xl lg:block">
            <div className="mx-auto max-w-[1600px] px-4 py-6">
              <div className="mb-4 flex items-baseline justify-between">
                <h3 className="text-sm font-bold text-primary-900">{t(CATEGORIES.find((c) => c.slug === stripCat)?.label ?? "")}</h3>
                <Link to={stripCat === "vacuumglas" ? "/vacuumglas" : `/categorie/${stripCat}`} onClick={() => setStripCat(null)} className="text-sm font-semibold text-accent-700 hover:text-accent-700">{t("Alles bekijken")} →</Link>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-10">
                {stripSections.slice(0, 10).map((sec, idx) => (
                  <Link
                    key={sec.title ?? idx}
                    to={sec.titleTo ?? `/categorie/${stripCat}`}
                    onClick={() => setStripCat(null)}
                    className="group flex flex-col rounded-xl border border-primary-100 bg-white p-2 transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-sm"
                  >
                    <div className="relative mb-2 aspect-square w-full overflow-hidden rounded-lg bg-white">
                      {sec.image ? (
                        <img src={cdnImage(sec.image, { w: IMG.card })} alt="" loading="lazy" decoding="async" className={`absolute inset-0 h-full w-full ${stripCat === "vacuumglas" ? "object-contain p-1" : "object-cover"} transition-transform duration-300 group-hover:scale-105`} />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-primary-200">
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
                        </div>
                      )}
                    </div>
                    <span className="line-clamp-2 text-xs font-medium text-primary-800 group-hover:text-primary-900">{t(sec.title ?? "")}</span>
                    {sec.soon && <span className="mt-0.5 text-[10px] font-semibold uppercase text-primary-600">{t("Binnenkort")}</span>}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* "Toegevoegd aan winkelwagen"-melding (bij de groene bestelknoppen). */}
      <CartToast />

      {/* Witte USP-balk (icoontjes + beloftes) boven de footer */}
      <UspRow variant="band" />

      <SiteFooter />

      {/* Juridische balk onder de footer */}
      <FooterLegalBar />

      {/* Cookie-/toestemmingsbanner (custom, AVG) */}
      <CookieBanner />

      {/* Zwevende "naar boven"-knop op lange pagina's */}
      <ScrollToTop />
    </div>
  );
}
